<template>
    <DCard
        :title="__('settings.orders.order_clients.WooCommerce')"
        padding
    >
        <DFormRow
            :label="__('settings.order_client.form.order_client')"
            :hint="__('settings.order_client.form.order_client_hint_woo_commerce')"
        >
            <DSwitch
                v-model="form.enabled"
                color="success"
                variant="3d"
                @update:modelValue="$emit('woo-commerce-valid', !form.enabled || credentialsAreValid)"
            />
        </DFormRow>
        <DRow v-if="form.enabled">
            <DFormRow
                :label="__('settings.order_client.form.api_url')"
                :is-required="true"
                :error="errorsData.api_url ?? errorsData['WooCommerce.api_url']"
                :hint="__('settings.order_client.form.api_url_hint_woo_commerce')"
            >
                <DInput
                    id="api_url"
                    v-model="form.api_url"
                    :placeholder="__('settings.order_client.form.api_url')"
                    :is-valid="isFormFieldValid('api_url')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.consumer_key')"
                :is-required="true"
                :error="errorsData.consumer_key ?? errorsData['WooCommerce.consumer_key']"
                :hint="__('settings.order_client.form.consumer_key_hint')"
            >
                <DInput
                    id="consumer_key"
                    v-model="form.consumer_key"
                    :placeholder="__('settings.order_client.form.consumer_key')"
                    :is-valid="isFormFieldValid('consumer_key')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.consumer_secret')"
                :is-required="true"
                :error="errorsData.consumer_secret ?? errorsData['WooCommerce.consumer_secret']"
                :hint="__('settings.order_client.form.consumer_secret_hint')"
            >
                <DInput
                    id="consumer_secret"
                    v-model="form.consumer_secret"
                    :placeholder="__('settings.order_client.form.consumer_secret')"
                    :is-valid="isFormFieldValid('consumer_secret')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.is_automatic_processing_enabled')"
                :is-required="true"
                :error="errorsData.process_new_customers ?? errorsData['WooCommerce.is_automatic_processing_enabled']"
                :hint="__('settings.order_client.form.is_automatic_processing_enabled_hint')"
            >
                <DSwitch
                    id="is_automatic_processing_enabled"
                    v-model="form.is_automatic_processing_enabled"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DRow>
                <DCol sm="4" />
                <DCol sm="4">
                    <div
                        v-if="connectionStatus === 'successful'"
                        class="green-text"
                    >
                        {{ __('settings.connections.connection_successful') }}
                    </div>
                    <div
                        v-if="connectionStatus === 'unsuccessful'"
                        class="red-text"
                    >
                        {{ __('settings.connections.connection_unsuccessful') }}
                    </div>
                </DCol>
                <DCol
                    sm="4"
                    class="check-database-connection-btn"
                >
                    <DButton
                        icon="lan-connect"
                        :label="__('settings.connections.check_connection')"
                        :disabled="isInLoadingState"
                        @click="checkConnection"
                    />
                </DCol>
                <DConnectionErrorModal
                    :show-modal="connectionException !== null"
                    :exception="connectionException"
                    @close-modal="closeErrorCheckModal"
                />
            </DRow>
        </DRow>
    </DCard>
</template>

<script setup>

import { route } from '~@/Composables/route'
import { ref, onUpdated } from 'vue'
import { useForm } from '@inertiajs/vue3'
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    wooCommerceSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const emit = defineEmits(['woo-commerce-valid', 'woo-commerce-data'])

const form = useForm({
    api_url: props.wooCommerceSettings.api_url ?? null,
    consumer_key: props.wooCommerceSettings.consumer_key,
    consumer_secret: props.wooCommerceSettings.consumer_secret,
    order_client: props.wooCommerceSettings.order_client ?? 'WooCommerce',
    enabled: props.wooCommerceSettings.enabled ?? false,
    is_automatic_processing_enabled: props.wooCommerceSettings.is_automatic_processing_enabled ?? false
})

const connectionStatus = ref('')
const apiError = ref(null)
const errorsData = ref(props.errors)
const credentialsAreValid = ref(false)
const connectionException = ref(null)

emit('woo-commerce-valid', !form.enabled || credentialsAreValid.value)
emit('woo-commerce-data', form)

const checkConnection = () => {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.order-settings.check-woo-commerce', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
            emit('woo-commerce-valid', !form.enabled || credentialsAreValid.value)
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false

            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
            if (error.response.data.api_error !== undefined) {
                connectionException.value = error.response.data.exception
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }
        })
}

const closeErrorCheckModal = () => {
    apiError.value = null
    connectionException.value = null
}

onUpdated(() => {
    errorsData.value = props.errors
})
</script>

<script>
export default {
    name: 'WooCommerceCreate'
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 12px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
